import React, { useState, useEffect } from "react";
import Header from "./components/Header";
import Introduction from "./components/Introduction";
import Projects from "./components/Projects";
import Contact from "./components/Contact";
import AboutMe from "./components/AboutMe";
import "./App.css";
import ReactGA from "react-ga";

const App = () => {
  const [activeSection, setActiveSection] = useState('home');
  const isNavigatingRef = React.useRef(false);
  const observerTimeoutRef = React.useRef(null);

  // Initialize Google Analytics only once
  useEffect(() => {
    ReactGA.initialize("UA-63454306-1");
    ReactGA.pageview(window.location.pathname + window.location.search);
    console.log(
      "%cHey there, curious person! Thanks for checking under the hood. This is my site. Built with care.\nHopefully there aren't any errors above these lines. 🙂\nIf you see some, send me an email, would you please?",
      "color: #4e54c8; font-size: 1.1em; line-height: 1.4;"
    );
  }, []);

  // Watch for sections entering viewport - no scroll calculations needed
  useEffect(() => {
    const checkActiveSection = () => {
      // Ignore updates during programmatic navigation
      if (isNavigatingRef.current) return;
      
      // Check all sections to see which is currently most visible
      const sections = ['home', 'about', 'projects', 'contact'];
      let mostVisible = null;
      let maxRatio = 0;
      
      sections.forEach(sectionId => {
        const section = document.getElementById(sectionId);
        if (section) {
          const rect = section.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const visibleTop = Math.max(0, -rect.top);
          const visibleBottom = Math.min(rect.height, viewportHeight - rect.top);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const ratio = visibleHeight / rect.height;
          
          if (ratio > maxRatio && rect.top < viewportHeight * 0.5) {
            maxRatio = ratio;
            mostVisible = sectionId;
          }
        }
      });
      
      if (mostVisible && ['home', 'about', 'projects', 'contact'].includes(mostVisible)) {
        setActiveSection(mostVisible);
      }
    };

    const observer = new IntersectionObserver(() => {
      // Clear any pending update
      if (observerTimeoutRef.current) {
        clearTimeout(observerTimeoutRef.current);
      }
      
      // Debounce: wait for scroll to settle, then check current state
      observerTimeoutRef.current = setTimeout(() => {
        checkActiveSection();
      }, 150); // Small debounce to prevent rapid updates
    }, {
      threshold: [0, 0.1, 0.5, 1.0], // Trigger at different visibility levels
      rootMargin: '-20% 0px -50% 0px' // Trigger when section is in upper portion of viewport
    });

    // Observe all sections - wait a bit for DOM to be ready
    const setupObserver = () => {
      const sections = ['home', 'about', 'projects', 'contact'];
      sections.forEach(sectionId => {
        const section = document.getElementById(sectionId);
        if (section) {
          observer.observe(section);
        }
      });
    };

    // Setup immediately and after a delay to catch late-loading content
    setupObserver();
    setTimeout(setupObserver, 100);

    return () => {
      const sections = ['home', 'about', 'projects', 'contact'];
      sections.forEach(sectionId => {
        const section = document.getElementById(sectionId);
        if (section) {
          observer.unobserve(section);
        }
      });
      if (observerTimeoutRef.current) {
        clearTimeout(observerTimeoutRef.current);
      }
    };
  }, []);

  // Handle hash changes (when clicking navigation links)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && ['home', 'about', 'projects', 'contact'].includes(hash)) {
        // Disable observer during navigation (only for actual hash changes, not initial load)
        isNavigatingRef.current = true;
        setActiveSection(hash);
        
        // Re-enable observer after scroll animation completes
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 1000);
      }
    };

    // Handle actual hash changes (user clicks)
    window.addEventListener('hashchange', handleHashChange);
    
    // On initial load, check hash but don't block observer
    const checkInitialHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && ['home', 'about', 'projects', 'contact'].includes(hash)) {
        setActiveSection(hash);
        // Don't block observer on initial load - let it take over immediately
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      }
    };
    
    // Check after DOM is ready
    setTimeout(checkInitialHash, 100);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return (
    <div className="app">
      <Header activeSection={activeSection} />
      <div id="home">
        <Introduction />
      </div>
      <div id="about">
        <AboutMe />
      </div>
      <div id="projects">
        <Projects />
      </div>
      <div id="contact">
        <Contact />
      </div>
    </div>
  );
};

export default App;
